import React, { useState } from 'react';
import { GitCompareIcon, SearchIcon, FilterIcon, DownloadIcon, CheckCircle2Icon, AlertCircleIcon, SparklesIcon, ArrowRightIcon, FileTextIcon, CreditCardIcon } from 'lucide-react';
import { mockSuggestions, mockExceptions, mockKpis, RecoSuggestion } from '../mocks/reconciliationData';
import { formatMoney } from '../../../shared/config/localeProfile';
import { ReconciliationDrawer } from '../components/ReconciliationDrawer';
export function Reconciliation() {
  const [activeTab, setActiveTab] = useState<'matches' | 'exceptions'>('matches');
  const [streamFilter, setStreamFilter] = useState<'all' | 'ar' | 'ap'>('all');
  const [selectedSuggestion, setSelectedSuggestion] = useState<RecoSuggestion | null>(null);
  const [suggestions, setSuggestions] = useState(mockSuggestions);
  const handleAccept = (id: string) => {
    setSuggestions(prev => prev.map(s => s.id === id ? {
      ...s,
      status: 'matched'
    } : s));
    setSelectedSuggestion(null);
  };
  const handleAcceptAllHighConfidence = () => {
    setSuggestions(prev => prev.map(s => s.confidence === 'high' && s.status === 'suggested' ? {
      ...s,
      status: 'matched'
    } : s));
  };
  const filteredSuggestions = suggestions.filter(s => {
    if (streamFilter === 'ar') return s.type === 'receivable';
    if (streamFilter === 'ap') return s.type === 'payable';
    return true;
  });
  const getConfidenceColor = (score?: number) => {
    if (!score) return 'bg-gray-100 text-gray-800';
    if (score >= 90) return 'bg-green-100 text-green-800';
    if (score >= 70) return 'bg-amber-100 text-amber-800';
    return 'bg-red-100 text-red-800';
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'matched':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-9016">
            Matched
          </span>;
      case 'suggested':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" data-id="element-9017">
            <SparklesIcon size={12} className="mr-1" data-id="element-9018" /> Suggested
          </span>;
      case 'partial':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800" data-id="element-9019">
            Partial
          </span>;
      default:
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-9020">
            {status}
          </span>;
    }
  };
  return <div className="max-w-[1600px] mx-auto space-y-6 pb-12" data-id="element-9021">
      {/* Header & KPIs */}
      <div className="flex flex-col gap-6" data-id="element-9022">
        <div className="flex items-center justify-between" data-id="element-9023">
          <div data-id="element-9024">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-id="element-9025">
              <GitCompareIcon className="text-gray-400" data-id="element-9026" />
              Reconciliation
            </h1>
            <p className="text-sm text-gray-500 mt-1" data-id="element-9027">
              Auto-match money movement against commercial documents.
            </p>
          </div>
          <div className="flex items-center gap-3" data-id="element-9028">
            <button className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2" data-id="element-9029">
              <DownloadIcon size={16} data-id="element-9030" />
              Export Journal
            </button>
            <button onClick={handleAcceptAllHighConfidence} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2" data-id="element-9031">
              <CheckCircle2Icon size={16} data-id="element-9032" />
              Accept High Confidence
            </button>
          </div>
        </div>

        {/* KPI Band */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-id="element-9033">
          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-id="element-9034">
            <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-9035">
              % Reconciled (Count)
            </h3>
            <div className="text-2xl font-bold font-mono text-gray-900" data-id="element-9036">
              {mockKpis.reconciledCountPct}%
            </div>
            <div className="w-full bg-gray-100 h-1.5 rounded-full mt-3" data-id="element-9037">
              <div className="bg-green-500 h-1.5 rounded-full" style={{
              width: `${mockKpis.reconciledCountPct}%`
            }} data-id="element-9038"></div>
            </div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-id="element-9039">
            <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-9040">
              % Reconciled (Value)
            </h3>
            <div className="text-2xl font-bold font-mono text-gray-900" data-id="element-9041">
              {mockKpis.reconciledValuePct}%
            </div>
            <div className="w-full bg-gray-100 h-1.5 rounded-full mt-3" data-id="element-9042">
              <div className="bg-green-500 h-1.5 rounded-full" style={{
              width: `${mockKpis.reconciledValuePct}%`
            }} data-id="element-9043"></div>
            </div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-id="element-9044">
            <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-9045">
              Unmatched Value
            </h3>
            <div className="text-2xl font-bold font-mono text-gray-900" data-id="element-9046">
              {formatMoney(mockKpis.unmatchedValue)}
            </div>
            <div className="mt-2 text-xs text-amber-600 font-medium" data-id="element-9047">
              Requires attention
            </div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-id="element-9048">
            <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-9049">
              Exceptions
            </h3>
            <div className="text-2xl font-bold font-mono text-gray-900" data-id="element-9050">
              {mockKpis.exceptionsCount}
            </div>
            <div className="mt-2 text-xs text-red-600 font-medium" data-id="element-9051">
              Action required
            </div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-id="element-9052">
            <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-9053">
              AI Auto-Matched
            </h3>
            <div className="text-2xl font-bold font-mono text-gray-900" data-id="element-9054">
              {formatMoney(mockKpis.autoMatchedValue)}
            </div>
            <div className="mt-2 text-xs text-blue-600 font-medium flex items-center gap-1" data-id="element-9055">
              <SparklesIcon size={12} data-id="element-9056" /> Saved 14 hours
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-9057">
        <div className="flex items-center gap-4" data-id="element-9058">
          <div className="flex bg-gray-100 p-1 rounded-lg" data-id="element-9059">
            <button onClick={() => setActiveTab('matches')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${activeTab === 'matches' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-9060">
              Matching Workspace
            </button>
            <button onClick={() => setActiveTab('exceptions')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-2 ${activeTab === 'exceptions' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-9061">
              Exceptions
              <span className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-xs" data-id="element-9062">
                {mockExceptions.length}
              </span>
            </button>
          </div>

          {activeTab === 'matches' && <div className="h-6 w-px bg-gray-300" data-id="element-9063"></div>}

          {activeTab === 'matches' && <div className="flex bg-gray-100 p-1 rounded-lg" data-id="element-9064">
              <button onClick={() => setStreamFilter('all')} className={`px-3 py-1 text-xs font-medium rounded-md ${streamFilter === 'all' ? 'bg-white shadow-sm' : 'text-gray-500'}`} data-id="element-9065">
                All Streams
              </button>
              <button onClick={() => setStreamFilter('ar')} className={`px-3 py-1 text-xs font-medium rounded-md ${streamFilter === 'ar' ? 'bg-white shadow-sm' : 'text-gray-500'}`} data-id="element-9066">
                AR (Receivables)
              </button>
              <button onClick={() => setStreamFilter('ap')} className={`px-3 py-1 text-xs font-medium rounded-md ${streamFilter === 'ap' ? 'bg-white shadow-sm' : 'text-gray-500'}`} data-id="element-9067">
                AP (Payables)
              </button>
            </div>}
        </div>

        <div className="flex items-center gap-3" data-id="element-9068">
          <div className="relative" data-id="element-9069">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} data-id="element-9070" />
            <input type="text" placeholder="Search reference, amount..." className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 w-64" data-id="element-9071" />
          </div>
          <button className="p-2 border border-gray-300 rounded-lg text-gray-500 hover:bg-gray-50" data-id="element-9072">
            <FilterIcon size={16} data-id="element-9073" />
          </button>
        </div>
      </div>

      {/* Main Workspace */}
      {activeTab === 'matches' && <div className="space-y-4" data-id="element-9074">
          {filteredSuggestions.map(suggestion => <div key={suggestion.id} onClick={() => setSelectedSuggestion(suggestion)} className="bg-white border border-gray-200 rounded-xl shadow-sm hover:border-blue-300 hover:shadow-md transition-all cursor-pointer overflow-hidden" data-id="element-9075">
              <div className="flex flex-col lg:flex-row" data-id="element-9076">
                {/* Left: Payments */}
                <div className="flex-1 p-5 bg-gray-50/50 border-b lg:border-b-0 lg:border-r border-gray-200" data-id="element-9077">
                  <div className="flex items-center justify-between mb-4" data-id="element-9078">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-9079">
                      Payments / Settlements
                    </span>
                    <span className="text-sm font-mono font-medium text-gray-900" data-id="element-9080">
                      {formatMoney(suggestion.payments.reduce((sum, p) => sum + p.grossAmount, 0))}
                    </span>
                  </div>
                  <div className="space-y-3" data-id="element-9081">
                    {suggestion.payments.map(p => <div key={p.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200" data-id="element-9082">
                        <div className="flex items-center gap-3" data-id="element-9083">
                          <CreditCardIcon size={16} className="text-gray-400" data-id="element-9084" />
                          <div data-id="element-9085">
                            <div className="text-sm font-medium text-gray-900" data-id="element-9086">
                              {p.reference}
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-9087">
                              {p.method}
                            </div>
                          </div>
                        </div>
                        <div className="text-right" data-id="element-9088">
                          <div className="text-sm font-mono text-gray-900" data-id="element-9089">
                            {formatMoney(p.grossAmount)}
                          </div>
                          <div className="text-xs text-gray-400" data-id="element-9090">
                            {new Date(p.date).toLocaleDateString()}
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>

                {/* Center: Match Link */}
                <div className="flex items-center justify-center p-4 bg-white relative" data-id="element-9091">
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-9092">
                    <div className="w-full h-px bg-gray-200" data-id="element-9093"></div>
                  </div>
                  <div className="relative z-10 flex flex-col items-center gap-2 bg-white px-4 py-2" data-id="element-9094">
                    {getStatusBadge(suggestion.status)}
                    {suggestion.confidenceScore && <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${getConfidenceColor(suggestion.confidenceScore)}`} data-id="element-9095">
                        {suggestion.confidenceScore}% Match
                      </span>}
                    <ArrowRightIcon size={16} className="text-gray-300" data-id="element-9096" />
                  </div>
                </div>

                {/* Right: Documents */}
                <div className="flex-1 p-5 bg-white" data-id="element-9097">
                  <div className="flex items-center justify-between mb-4" data-id="element-9098">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-9099">
                      Open Documents
                    </span>
                    <span className="text-sm font-mono font-medium text-gray-900" data-id="element-9100">
                      {formatMoney(suggestion.documents.reduce((sum, d) => sum + d.amount, 0))}
                    </span>
                  </div>
                  <div className="space-y-3" data-id="element-9101">
                    {suggestion.documents.map(d => <div key={d.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200" data-id="element-9102">
                        <div className="flex items-center gap-3" data-id="element-9103">
                          <FileTextIcon size={16} className="text-gray-400" data-id="element-9104" />
                          <div data-id="element-9105">
                            <div className="text-sm font-medium text-gray-900" data-id="element-9106">
                              {d.reference}
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-9107">
                              {d.counterparty}
                            </div>
                          </div>
                        </div>
                        <div className="text-right" data-id="element-9108">
                          <div className="text-sm font-mono text-gray-900" data-id="element-9109">
                            {formatMoney(d.amount)}
                          </div>
                          <div className="text-xs text-gray-400" data-id="element-9110">
                            Due {new Date(d.dueDate).toLocaleDateString()}
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>
            </div>)}
        </div>}

      {/* Exceptions Tab */}
      {activeTab === 'exceptions' && <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-9111">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-9112">
            <thead className="bg-gray-50" data-id="element-9113">
              <tr data-id="element-9114">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9115">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9116">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9117">
                  Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9118">
                  Amount
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9119">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-9120">
              {mockExceptions.map(exc => <tr key={exc.id} className="hover:bg-gray-50" data-id="element-9121">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-9122">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-red-50 text-red-700 text-xs font-medium" data-id="element-9123">
                      <AlertCircleIcon size={14} data-id="element-9124" />
                      {exc.type.replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4" data-id="element-9125">
                    <div className="text-sm text-gray-900" data-id="element-9126">
                      {exc.description}
                    </div>
                    <div className="text-xs text-gray-500 mt-1" data-id="element-9127">
                      Suggestion: {exc.suggestedResolution}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-9128">
                    {new Date(exc.date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-right font-medium text-gray-900" data-id="element-9129">
                    {formatMoney(exc.amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-9130">
                    <button className="text-blue-600 hover:text-blue-900" data-id="element-9131">
                      Resolve
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>}

      <ReconciliationDrawer suggestion={selectedSuggestion} onClose={() => setSelectedSuggestion(null)} onAccept={handleAccept} data-id="element-9132" />
    </div>;
}